import { Injectable, Injector, Signal, computed, inject, signal } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { IrisDMSFolderI, IrisDmsRecentFilesFolder, IrisDmsRootFolderId } from '../models/IrisDMSFolder';
import { Observable, Subject } from 'rxjs';
import { shareReplay, tap, take, filter, map } from 'rxjs/operators';
import { IrisInfrastructureService } from '@iris/common/services/infrastructure.service';
import {
  SetSelectedFolder,
  ResetModalPagePagination,
} from './actions/dms-global.actions';
import { ImportingFolderTreeStage, ImportingFolderTreeStatus } from '@iris/common/modules/dms/models/ImportingFolderTree';
import { TooltipPosition } from '@angular/material/tooltip';
import { DmsWebsocketService } from '@iris/common/services/websockets/dms-websocket.service';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { selectQueryParams, selectRouteParam } from '@iris/common/redux/router.selectors';
import { IrisLocalStorageService } from '@iris/common/services/utils/iris-local-storage.service';
import { IrisDmsTrashBinStore } from '../store/trash-bin.signal-store';
import { toObservable } from '@angular/core/rxjs-interop';
import { IrisDmsSystemFoldersStore } from '../store/system-folders.signal-store';
import { IrisDmsFoldersStore } from '../store/folders.signal-store';
import { IrisFoldersService } from '@iris/common/services/docs/folders.service';
import { ToastService } from '@ngx-strakit/cdk/controls/toast';
import { TranslateService } from '@ngx-translate/core';
import { IrisDmsGlobalStore } from '../store/dms-global.signal-store';

@Injectable({ providedIn: 'root' })
export class IrisDmsFoldersSandbox {
  private readonly store = inject(Store);
  private readonly infrastructureService = inject(IrisInfrastructureService);
  private readonly dmsWebsocketService = inject(DmsWebsocketService);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly localStorageService = inject(IrisLocalStorageService);
  private readonly foldersService = inject(IrisFoldersService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly injector = inject(Injector);

  private readonly dmsTrashBinSotre = inject(IrisDmsTrashBinStore);
  private readonly dmsSystemFoldersStore = inject(IrisDmsSystemFoldersStore);
  private readonly dmsFoldersStore = inject(IrisDmsFoldersStore);
  private readonly dmsGlobalStore = inject(IrisDmsGlobalStore);

  readonly folderTooltipPosition: TooltipPosition = 'after';

  readonly filesAndFoldersContentTypeLocalStorageKey = 'dmsFileViewSettings';
  readonly trashBinContentTypeLocalStorageKey = 'dmsTrashBinContentViewType';
  private readonly foldersTreeWidthLocalStorageKey = 'dms-folders-tree-width';

  private readonly defaultFolderTreeWidth = 300;

  private readonly currentProjectId = this.globalSandbox.currentProjectId;
  private readonly currentProjectIdModal = this.dmsGlobalStore.currentProjectIdModal;

  readonly foldersLoading = computed(() => this.dmsFoldersStore.loading() || this.dmsSystemFoldersStore.loading());

  readonly selectedFolderPath = computed(() => {
    const selectedFolder = this.selectedFolder();
    if (!selectedFolder) {
      return [];
    }
    if (selectedFolder.isSystem) {
      return this.dmsSystemFoldersStore.getFolderPathByFolderId(selectedFolder.id)();
    }
    return this.dmsFoldersStore.getFolderPathByFolderId(selectedFolder.id)();
  });

  readonly folderTreeWidth = signal(this.localStorageService.getItem(this.foldersTreeWidthLocalStorageKey) || this.defaultFolderTreeWidth);

  readonly showFoldersTree = this.dmsGlobalStore.showFoldersTree;
  readonly isModalView = this.dmsFoldersStore.isModalView;
  readonly hasFolders = this.dmsFoldersStore.hasFolders;

  readonly systemFolders = this.dmsSystemFoldersStore.entities;

  readonly selectedFolderId = this.dmsGlobalStore.selectedFolderId;
  readonly selectedFolderId$ = toObservable(this.selectedFolderId);
  readonly selectedFolder = computed(() => {
    const selectedFolderId = this.selectedFolderId();

    return this.dmsFoldersStore.getFolderById(selectedFolderId)()
      || this.dmsSystemFoldersStore.getFolderById(selectedFolderId)();
  });
  readonly selectedFolder$ = toObservable(this.selectedFolder).pipe(
    filter(Boolean),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );

  readonly selectedFolderIdModal = this.dmsGlobalStore.selectedFolderIdModal;
  readonly selectedFolderIdModal$ = toObservable(this.selectedFolderIdModal);

  readonly selectedFolderModal = computed(() => {
    const selectedFolderId = this.selectedFolderIdModal();

    return this.dmsFoldersStore.getFolderById(selectedFolderId)()
      || this.dmsSystemFoldersStore.getFolderById(selectedFolderId)();
  });

  readonly selectedFolderModal$ = toObservable(this.selectedFolderModal);

  readonly selectedFoldersIdsModal = this.dmsGlobalStore.selectedFoldersIdsModal;
  readonly selectedFoldersListModal = computed(() => {
    const selectedFoldersIdsModal = this.selectedFoldersIdsModal();
    return this.dmsFoldersStore.getFoldersByIds(selectedFoldersIdsModal);
  });

  readonly hasSelectedFolder = computed(() => !!this.selectedFolderId());
  readonly isRecentFilesFolderSelected = computed(() => !!this.selectedFolder()?.isRecentFilesFolder);
  readonly isSelectedFolderLocked = computed(() => {
    const selectedFolder = this.selectedFolder();
    return !!selectedFolder?.isLocked || !!selectedFolder?.isParentLocked;
  });

  readonly isSelectedFolderNotLocked = computed(() => {
    const selectedFolderId = this.selectedFolderId();
    return !selectedFolderId || !this.isSelectedFolderLocked();
  });

  readonly openedMenuFolderId = this.dmsGlobalStore.openedMenuFolderId;

  readonly folderViewSelectedTab = this.dmsGlobalStore.folderViewSelectedTab;
  readonly trashBinTabSelected = this.dmsGlobalStore.trashBinTabSelected;
  readonly tagsTabSelected = this.dmsGlobalStore.tagsTabSelected;
  readonly foldersTabSelected = this.dmsGlobalStore.foldersTabSelected;
  readonly systemFoldersTabSelected = this.dmsGlobalStore.systemFoldersTabSelected;
  readonly wrongRequestedFolderId = this.dmsFoldersStore.wrongRequestedFolderId;
  readonly wrongRequestedFolderId$ = toObservable(this.wrongRequestedFolderId);

  private readonly isSelectedFolderTrashBin = computed(() => !!this.selectedFolder()?.isTrashBin);
  readonly trashBinSelected = computed(() => this.isSelectedFolderTrashBin() || this.trashBinTabSelected());

  readonly contentTypeLocalStorageKey = computed(() =>
    this.trashBinTabSelected() ? this.trashBinContentTypeLocalStorageKey : this.filesAndFoldersContentTypeLocalStorageKey,
  );

  readonly importingFolderTrees = this.dmsGlobalStore.importingFolderTrees;
  folderTreeImportingCancellation$ = new Subject<void>();

  routeQueryParams$ = this.store.pipe(select(selectQueryParams));
  routeParamsFolderId$ = this.store.pipe(select(selectRouteParam('folderId')));

  readonly getRootFoldersByProjectId = (projectId: number): Signal<IrisDMSFolderI[]> => this.dmsFoldersStore.getFoldersByParentId(IrisDmsRootFolderId, projectId, false);

  readonly getProjectRootFolderById = (projectId: number): Signal<IrisDMSFolderI> => computed(() => {
    const rootFolders = this.getRootFoldersByProjectId(projectId)() ?? [];
    return rootFolders[0];
  });

  readonly getProjectRootFolderById$ = (projectId: number): Observable<IrisDMSFolderI> =>
    toObservable(this.getProjectRootFolderById(projectId), { injector: this.injector }).pipe(
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );

  readonly currentProjectRootFolder = computed(() => this.getProjectRootFolderById(this.getCurrentProjectId())());
  readonly currentProjectRootFolder$ = toObservable(this.currentProjectRootFolder);
  readonly currentProjectRootFolderId = computed(() => this.currentProjectRootFolder()?.id);
  readonly currentProjectRootFolderId$ = toObservable(this.currentProjectRootFolderId);

  private readonly REMOVE_COMPLETED_FOLDER_TREE_UPLOAD_DELAY = 30000;

  getSiblingFoldersById(folderId: string): Signal<IrisDMSFolderI[]> {
    const folder = this.getFolderById(folderId)();

    if (folder?.isSystem) {
      return this.dmsSystemFoldersStore.getFoldersByParentId(folder.parentId);
    }
    return this.dmsFoldersStore.getFoldersByParentId(folder.parentId);
  };

  getSubfolders(parentId: string, isSystem = false, isModalBackground = false): Signal<IrisDMSFolderI[]> {
    if (isSystem) {
      return this.dmsSystemFoldersStore.getSubfolders(parentId);
    }

    const currentProjectId = this.getCurrentProjectId(isModalBackground);
    return this.dmsFoldersStore.getFoldersByParentId(parentId, currentProjectId, isModalBackground);
  }

  getSubfolders$(parentId: string, isSystem = false, isModalBackground = false): Observable<IrisDMSFolderI[]> {
    const subfolders = this.getSubfolders(parentId, isSystem, isModalBackground);
    return toObservable(subfolders, { injector: this.injector }).pipe(
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );
  }

  buildVisibleFlatFoldersTree(isSystem = false, isModalBackground = false): Signal<IrisDMSFolderI[]> {
    return computed(() => {
      if (isSystem) {
        return this.dmsSystemFoldersStore.buildVisibleFlatFoldersTree();
      }
      const currentProjectId = this.getCurrentProjectId(isModalBackground);
      return this.dmsFoldersStore.buildVisibleFlatFoldersTree(currentProjectId, isModalBackground)();
    });
  }

  buildVisibleFlatFoldersTree$(isSystem = false, isModalBackground = false): Observable<IrisDMSFolderI[]> {
    const visibleFoldersTree = this.buildVisibleFlatFoldersTree(isSystem, isModalBackground);
    return toObservable(visibleFoldersTree, { injector: this.injector }).pipe(
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );
  }

  getFolderById(folderId: string): Signal<IrisDMSFolderI> {
    return computed(() => {
      return this.dmsFoldersStore.getFolderById(folderId)()
        || this.dmsTrashBinSotre.getFolderById(folderId)()
        || this.dmsSystemFoldersStore.getFolderById(folderId)();
    });
  }

  getFolderById$(folderId: string): Observable<IrisDMSFolderI> {
    return toObservable(this.getFolderById(folderId), { injector: this.injector });
  }

  getFoldersByIds(foldersIds: string[]): IrisDMSFolderI[] {
    return this.dmsFoldersStore.getFoldersByIds(foldersIds);
  }

  getSystemFolders(): void {
    this.dmsSystemFoldersStore.loadNextPage();
  }

  getParentFolders(folderId: string, expandFoldersTree = false): void {
    if (!folderId || folderId === IrisDmsRecentFilesFolder.id) {
      return;
    }
    const folder = this.getFolderById(folderId)();
    if (folder?.isSystem) {
      this.dmsSystemFoldersStore.fetchParentFolder$(folderId);
      return;
    }
    this.dmsFoldersStore.fetchParentFolders$({ folderId, expandFoldersTree });
  }

  setSelectedFolder(folderId: string, isHideFileSidebar = false, expandFoldersTree = false): void {
    this.store.dispatch(SetSelectedFolder({ folderId, isHideFileSidebar, expandFoldersTree }));
  }

  setSelectedFolderModal(folderId: string, resetPaginationOnFolderSelect = false): void {
    this.dmsGlobalStore.setSelectedModalFolderId(folderId);
    if (resetPaginationOnFolderSelect) {
      setTimeout(() => {
        this.store.dispatch(ResetModalPagePagination());
      });
    }
  }

  changeProjectFolderModal(projectId: number): void {
    this.dmsGlobalStore.setSelectedModalProjectId(projectId);
  }

  toggleFolderInSelectedListModal(folderId: string): void {
    this.dmsGlobalStore.toggleFolderInSelectedListModal(folderId);
  }

  setSelectedFoldersIdsModal(foldersIds: string[]): void {
    this.dmsGlobalStore.setSelectedFoldersIdsModal(foldersIds);
  }

  resetSelectedFoldersListModal(): void {
    this.dmsGlobalStore.resetSelectedFoldersIdsModal();
  }

  expandFolder(folderId: string, isSystem = false): void {
    if (isSystem) {
      this.dmsSystemFoldersStore.expandFolder$(folderId);
      return;
    }
    this.dmsWebsocketService.subscribeToFolder(folderId);
    this.dmsFoldersStore.expandFolder$(folderId);
  }

  unExpandFolder(folderId: string, isSystem = false): void {
    if (isSystem) {
      this.dmsSystemFoldersStore.collapseFolder(folderId);
      return;
    }
    this.dmsWebsocketService.unsubscribeFromFolder(folderId);
    this.dmsFoldersStore.collapseFolder(folderId);
  }

  collapseAllFolders(): void {
    this.dmsFoldersStore.collapseAllFolders();
  }

  getFolder(folderId: string): void {
    this.dmsFoldersStore.fetchFolderById$(folderId);
  }

  createFolder(folder: Partial<IrisDMSFolderI>, parentFolderId: string, setSelected = false): void {
    this.dmsFoldersStore.createFolder$({ folder, parentFolderId, setSelected });
  }

  updateFolder(folder: IrisDMSFolderI): void {
    this.dmsFoldersStore.updateFolder$(folder);
  }

  removeFolder(folderId: string): void {
    this.dmsFoldersStore.removeFolder$({ folderId });
  }

  removeFoldersFromStore(fodlersIds: string[]): void {
    this.dmsFoldersStore.removeFolders(fodlersIds);
  }

  setFoldersTreeWidth(width: number): void {
    this.localStorageService.setItem(this.foldersTreeWidthLocalStorageKey, width);
    this.folderTreeWidth.set(width);
  }

  copyToClipboard(value: string): void {
    this.infrastructureService.copyToClipboard(value);
  }

  resetSelectedFolderId(): void {
    this.dmsGlobalStore.resetSelectedFolderId();
  }

  setOpenedMenuFolderId(folderId: string): void {
    this.dmsGlobalStore.setOpenedMenuFolderId(folderId);
  }

  addImportingFolderTree(importingFolderTree: ImportingFolderTreeStatus): void {
    this.dmsGlobalStore.addImportingFolderTree(importingFolderTree);
  }

  updateImportingFolderTree(importingFolderTree: ImportingFolderTreeStatus): void {
    this.dmsGlobalStore.updateImportingFolderTree(importingFolderTree);
    if (importingFolderTree.stage === ImportingFolderTreeStage.Completed) {
      setTimeout(() => {
        this.removeImportingFolderTree(importingFolderTree.name);
      }, this.REMOVE_COMPLETED_FOLDER_TREE_UPLOAD_DELAY);
    }
  }

  removeImportingFolderTree(folderName: string): void {
    this.dmsGlobalStore.removeImportingFolderTree(folderName);
  }

  resetImportingFolderTrees(): void {
    this.dmsGlobalStore.resetImportingFolderTrees();
  }

  getProjectRootFolder(projectId: number): void {
    this.dmsFoldersStore.fetchProjectRootFolder$(projectId);
  }

  resetFolders(): void {
    this.dmsFoldersStore.reset();
  }

  toggleFoldersTreeVisibility(force?: boolean): void {
    this.dmsGlobalStore.toggleFoldersTreeVisibility(force);
  }

  isRecentFilesFolder(folderId: string): boolean {
    return folderId === IrisDmsRecentFilesFolder.id;
  }

  unzipFile(fileId: string): void {
    const folderId = this.selectedFolderId();
    this.foldersService.unzipFile(folderId, fileId).pipe(
      take(1),
      tap(() => {
        this.toastService.success({ message: this.translateService.instant('text.dms.FileUnzipRequested') });
      }),
    ).subscribe();
  }

  public getCurrentProjectId(isModalBackground = false): number {
    const projectId = this.currentProjectId();
    const projectIdModal = this.currentProjectIdModal();
    return isModalBackground || this.dmsFoldersStore.isModalView() ? projectIdModal : projectId;
  }

  resetWrongRequestedFolderId(): void {
    this.dmsFoldersStore.resetWrongRequestedFolderId();
  }

  getUploadFolderStartWith(projectRootFolder: IrisDMSFolderI, uploadFolderPath:string): Observable<IrisDMSFolderI> {
    return this.foldersService.getProjectSubFolderByPattern(projectRootFolder.path.concat(uploadFolderPath))
      .pipe( map((subFolder) => subFolder[0] ?? null));
  }

}
